import React, { useContext, useState } from 'react';

import { Button, Spinner } from '@ergeon/core-components';
import { PhoneIcon } from '@ergeon/icons/hero/micro';

import { CustomerDetail } from '../../../types';
import { TwilioContext } from '../../../context-providers/TwilioContext';
import { formatPhoneNumber } from '../utils/formatUtils';

type PhoneItem = {
  phone: string;
  rawPhone: string;
  isPrimary: boolean;
};

const combinePhones = (
  phoneNumber: string,
  primaryPhone: CustomerDetail['primary_phone'],
  additionalPhones: CustomerDetail['additional_phones'],
): PhoneItem[] => {
  return [
    phoneNumber && {
      phone: formatPhoneNumber(phoneNumber),
      rawPhone: primaryPhone?.identifier || phoneNumber,
      isPrimary: true,
    },
    ...(additionalPhones || [])
      .filter((p) => p && p.identifier)
      .map((p) => ({
        phone: formatPhoneNumber(p.identifier),
        rawPhone: p.identifier,
        isPrimary: false,
      })),
  ].filter(Boolean) as PhoneItem[];
};

type PhoneButtonsProps = {
  phoneNumber: string;
  primaryPhone: CustomerDetail['primary_phone'];
  additionalPhones: CustomerDetail['additional_phones'];
};

export const PhoneButtons: React.FC<PhoneButtonsProps> = ({ phoneNumber, primaryPhone, additionalPhones }) => {
  const { makeCall } = useContext(TwilioContext);
  const [callingPhone, setCallingPhone] = useState<string | null>(null);
  const phones = combinePhones(phoneNumber, primaryPhone, additionalPhones);

  if (phones.length === 0) return null;

  const handleCall = async(rawPhone: string) => {
    if (callingPhone) return; // Prevent multiple clicks

    setCallingPhone(rawPhone);
    try {
      await makeCall(rawPhone);
    } finally {
      setCallingPhone(null);
    }
  };

  return (
    <div className="flex gap-1">
      {phones.map((item, index) => {
        const isLoading = callingPhone === item.rawPhone;
        return (
          <Button
            className="max-w-min text-nowrap"
            data-test="call-button"
            disabled={isLoading}
            flavor="success"
            key={index}
            onClick={() => handleCall(item.rawPhone)}
            size="medium"
            taste={item.isPrimary ? 'solid' : 'line'}>
            {isLoading ? <Spinner active className="shrink-0" /> : <PhoneIcon className="size-4 shrink-0" />}
            {item.phone}
          </Button>
        );
      })}
    </div>
  );
};
